import { AppModule } from 'app/module';
import { Input } from 'module/input/main';
import { Autocomplete } from 'module/autocomplete/main';
import { PillList } from 'module/pillList/main';
import { KEYCODE_ENTER } from 'app/keyCodes';
import { generic } from 'util/validationMessages';
import { serializeJson } from 'util/form';
import { postJson } from 'util/xhr';
import { GdprModal } from 'module/gdprModal/main';
import { trackGa4 } from 'util/googleAnalytics';
import { JobsInfoBox } from 'module/jobsInfoBox/main';
import {
    API_ENDPOINT_AUTOCOMPLETE_KEYWORDS,
    API_ENDPOINT_AUTOCOMPLETE_LOCATIONS,
    API_ENDPOINT_AUTOCOMPLETE_SKILLS,
    API_ENDPOINT_JOB_MATCHING_CREATE_PROFILE,
} from 'app/endpoints';
import Template from './template.hbs';
import './styles.scss';

const minimumSkillCount = 3;
const maximumPillListItems = 25;

export class JobMatchingEmptyStateForm extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    domBindings() {
        return {
            form: '.m-jobMatchingEmptyStateForm__form',
            desiredJobs: '.m-jobMatchingEmptyStateForm__desiredJobs .m-input',
            desiredJobsAutocomplete: '.m-jobMatchingEmptyStateForm__desiredJobsAutocomplete .m-autocomplete',
            desiredJobsPillList: '.m-jobMatchingEmptyStateForm__desiredJobsPillList .m-pillList',
            desiredLocations: '.m-jobMatchingEmptyStateForm__desiredLocations .m-input',
            desiredLocationsAutocomplete: '.m-jobMatchingEmptyStateForm__desiredLocationsAutocomplete .m-autocomplete',
            desiredLocationsPillList: '.m-jobMatchingEmptyStateForm__desiredLocationsPillList .m-pillList',
            skills: '.m-jobMatchingEmptyStateForm__skills .m-input',
            skillsAutocomplete: '.m-jobMatchingEmptyStateForm__skillsAutocomplete .m-autocomplete',
            skillsPillList: '.m-jobMatchingEmptyStateForm__skillsPillList .m-pillList',
            email: '.m-jobMatchingEmptyStateForm__email .m-input',
            gdprModal: '.m-jobMatchingEmptyStateForm__gdprModal .m-gdprModal',
            mailInputHeadline: '.m-jobMatchingEmptyStateForm__mailInputHeadline',
            submitButton: '.m-jobMatchingEmptyStateForm__submit',
            generalErrorBoxContainer: '.m-jobMatchingEmptyStateForm__generalError',
            generalErrorBox: '.m-jobMatchingEmptyStateForm__generalError .m-jobsInfoBox',
        };
    }

    getPropsFromDom() {
        return {
            desiredJobs: this.dom.form ? this.dom.form.desiredJobs.value : '',
            desiredLocations: this.dom.form ? this.dom.form.desiredLocations.value : '',
            skills: this.dom.form ? this.dom.form.skills.value : '',
            email: this.dom.form ? this.dom.form.email.value : '',
            isLoggedIn: !this.dom.mailInputHeadline,
            submitButtonText: this.dom.submitButton ? this.dom.submitButton.innerText : '',
            generalErrorBoxProps: this.generalErrorBox ? this.generalErrorBox.getPropsFromDom() : [],
        };
    }

    subs() {
        if (this.dom.desiredJobs) {
            this.desiredJobs = new Input(this.dom.desiredJobs);

            if (this.dom.desiredJobsAutocomplete) {
                this.desiredJobsAutocomplete = new Autocomplete(this.dom.desiredJobsAutocomplete);
                this.desiredJobsAutocomplete.set({
                    input: this.desiredJobs,
                    endpoint: API_ENDPOINT_AUTOCOMPLETE_KEYWORDS,
                });
            }
        }

        if (this.dom.desiredLocations) {
            this.desiredLocations = new Input(this.dom.desiredLocations);

            if (this.dom.desiredLocationsAutocomplete) {
                this.desiredLocationsAutocomplete = new Autocomplete(this.dom.desiredLocationsAutocomplete);
                this.desiredLocationsAutocomplete.set({
                    input: this.desiredLocations,
                    endpoint: API_ENDPOINT_AUTOCOMPLETE_LOCATIONS,
                });
            }
        }

        if (this.dom.skills) {
            this.skills = new Input(this.dom.skills);

            if (this.dom.skillsAutocomplete) {
                this.skillsAutocomplete = new Autocomplete(this.dom.skillsAutocomplete);
                this.skillsAutocomplete.set({
                    input: this.skills,
                    endpoint: API_ENDPOINT_AUTOCOMPLETE_SKILLS,
                });
            }
        }

        if (this.dom.email) {
            this.email = new Input(this.dom.email);
        }
        if (this.dom.desiredJobsPillList) {
            this.desiredJobsPillList = new PillList(this.dom.desiredJobsPillList);
        }
        if (this.dom.desiredLocationsPillList) {
            this.desiredLocationsPillList = new PillList(this.dom.desiredLocationsPillList);
        }
        if (this.dom.skillsPillList) {
            this.skillsPillList = new PillList(this.dom.skillsPillList);
        }
        if (this.dom.gdprModal) {
            this.gdprModal = new GdprModal(this.dom.gdprModal);
        }
        if (this.dom.generalErrorBox) {
            this.generalErrorBox = new JobsInfoBox(this.dom.generalErrorBox);
        }
    }

    subEvents() {
        if (this.desiredJobs) {
            this.desiredJobs.events.on('actionIconClicked', () => {
                this.addItemToPillList(this.desiredJobs, this.desiredJobsPillList);
            });
            this.desiredJobs.events.on('keyup', (e) => {
                // Check for active autocomplete to prevent inserting both on enter,
                // the input value and the currently selected autocomplete
                if (e.arguments[0] === KEYCODE_ENTER && this.desiredJobsAutocomplete.props.selectedIndex === false) {
                    this.addItemToPillList(this.desiredJobs, this.desiredJobsPillList);
                }
            });
            // Triggers when user chooses item via autocomplete
            this.desiredJobsAutocomplete.events.on('autoCompleteItemClicked', (e) => {
                e.stopPropagation();
                this.addItemToPillList(this.desiredJobs, this.desiredJobsPillList);
            });

            // Triggers when input loses focus or gets removed from the DOM(!)
            this.desiredJobs.events.on('blur', () => {
                setTimeout(() => {
                    this.addItemToPillList(this.desiredJobs, this.desiredJobsPillList);
                }, 0);
            });

            this.desiredJobsPillList.events.on('itemsChanged', (e) => {
                this.updateProps({ desiredJobsItems: e.arguments[0] });
            });
        }

        if (this.desiredLocations) {
            // Triggers when user chooses item via autocomplete
            this.desiredLocationsAutocomplete.events.on('autoCompleteItemClicked', (e) => {
                const desiredLocationId = e.arguments[0];
                this.addItemToPillList(this.desiredLocations, this.desiredLocationsPillList, 1, desiredLocationId);
            });

            this.desiredLocations.events.on('keyup', (e) => {
                if (e.arguments[0] === KEYCODE_ENTER
                    && this.desiredLocationsAutocomplete.props.selectedIndex === false) {
                    this.handleDesiredLocationInputLogic();
                }
            });

            this.desiredLocations.events.on('actionIconClicked', () => {
                if (this.desiredLocationsAutocomplete.props.selectedIndex === false) {
                    this.handleDesiredLocationInputLogic();
                }
            });

            this.desiredLocationsPillList.events.on('itemsChanged', (e) => {
                this.updateProps({ desiredLocationsItems: e.arguments[0] });
            });
        }

        if (this.skills) {
            this.skills.events.on('actionIconClicked', () => {
                this.addItemToPillList(this.skills, this.skillsPillList, minimumSkillCount);
            });
            this.skills.events.on('keyup', (e) => {
                // Check for active autocomplete to prevent inserting both on enter,
                // the input value and the currently selected autocomplete
                if (e.arguments[0] === KEYCODE_ENTER && this.skillsAutocomplete.props.selectedIndex === false) {
                    this.addItemToPillList(this.skills, this.skillsPillList, minimumSkillCount);
                }
            });
            // Triggers when user chooses item via autocomplete
            this.skillsAutocomplete.events.on('autoCompleteItemClicked', (e) => {
                e.stopPropagation();
                this.addItemToPillList(this.skills, this.skillsPillList, minimumSkillCount);
            });

            // Triggers when input loses focus
            this.skills.events.on('blur', () => {
                setTimeout(() => {
                    this.addItemToPillList(this.skills, this.skillsPillList);
                }, 0);
            });

            this.skillsPillList.events.on('itemsChanged', (e) => {
                this.updateProps({ skillsItems: e.arguments[0] });
            });
        }
        if (this.email) {
            this.email.events.on('actionIconClicked', () => this.email.resetValue());
            this.email.events.on('blur', () => this.updateProps({ email: this.email.getValue() }));
        }
        if (this.gdprModal) {
            this.gdprModal.events.on('acceptGdpr', () => this.acceptGdpr());
            this.gdprModal.events.on('removeModalStyle', () => this.closeGdprModal());
        }
    }

    handleDesiredLocationInputLogic() {
        const { items } = this.desiredLocationsAutocomplete.props;
        // Check if input value exists in autocomplete matches
        const match = (items || [])?.find(({ label }) => label === this.desiredLocations.getValue());
        if (match) {
            this.addItemToPillList(this.desiredLocations, this.desiredLocationsPillList, 1, match.locationId);
        } else if (items && items.length > 0) {
            this.desiredLocations.setValue(items[0].label);
            this.addItemToPillList(this.desiredLocations, this.desiredLocationsPillList, 1, items[0].locationId);
        } else {
            this.desiredLocations.setInvalid([{ unknownLocation: true }]);
        }
    }

    domEvents() {
        // Don't listen on submit, otherwise hitting enter in input fields would trigger the form
        this.dom.submitButton.addEventListener('click', (e) => this.submitForm(e));
    }

    submitForm(e) {
        if (e) e.preventDefault();

        if (this.validate()) {
            this.createMatching(serializeJson(this.dom.form));
        }
    }

    createMatching(formData) {
        if (this.request) {
            this.request.abort();
        }

        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = true;
        }

        const data = this.prepareListData(formData);
        this.request = postJson(
            API_ENDPOINT_JOB_MATCHING_CREATE_PROFILE,
            { data },
            (response) => this.onResponse(response),
            (error) => this.onResponseError(error),
        );
    }

    prepareListData(formData) {
        formData.desiredJobs = this.desiredJobsPillList.reduceListToArrays();
        formData.desiredLocations = this.desiredLocationsPillList.getAllIds();
        formData.skills = this.skillsPillList.reduceListToArrays();

        return formData;
    }

    onResponse(response) {
        if (response.gdpr) {
            this.gdprModal.setProps(response.gdpr);
            this.gdprModal.updateProps({ visible: true, scrollTop: 0, active: true }, true);
            this.gdprModal.checkIfScrollEnd();
            this.events.emit('addModalStyle');
        } else if (response.success) {
            trackGa4({
                event: 'matching_profile_create_success',
                user_data: {
                    email: this.email.getValue(),
                },
            });

            if (response.profileCreated) {
                // Refresh page, so user gets redirected to splitview
                const url = `${window.location.href}?profileCreated=1`;
                window.location.replace(url);
            } else {
                this.events.emit('showSuccessState', response);
            }
        }
    }

    onResponseError(response) {
        const errorsMeta = this.validation.handleBackendValidationErrors(response);

        // Show general error box above form
        if (errorsMeta.general?.[0].job_matching_failed) {
            this.showGeneralErrorBox();
        } else {
            // Show resultState
            this.events.emit('createMatchingFailure', { error: errorsMeta });
        }

        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = false;
        }

        return true;
    }

    validationRules() {
        this.validation.useMessages(generic);
        // Gets called before validation starts
        this.validation.before(() => { this.formErrorLabels = []; });

        this.validation.addRule(this.email, {
            sequential: true,
            rules: [{
                name: 'required',
                message: generic.email,
                sideEffects: ({ valid }) => {
                    if (!valid) this.formErrorLabels.push('no-email');
                },
            }, {
                name: 'email',
                sideEffects: ({ valid, value }) => {
                    if (!valid && value !== '') this.formErrorLabels.push('invalid-email');
                },
            }],
        });

        this.validation.addRule(this.desiredJobs, {
            rules: [{
                name: 'required',
                test: () => this.desiredJobsPillList?.props.items?.length > 0,
                message: 'Gib bitte mindestens einen Wunschjob an.',
                sideEffects: ({ valid }) => {
                    if (!valid) this.formErrorLabels.push('no-job');
                },
            }],
        });

        this.validation.addRule(this.desiredLocations, {
            rules: [{
                name: 'required',
                test: () => this.desiredLocationsPillList?.props.items?.length > 0,
                message: 'Gib bitte mindestens einen Wunscharbeitsort an.',
                sideEffects: ({ valid }) => {
                    if (!valid) this.formErrorLabels.push('no-location');
                },
            }],
        });

        this.validation.addRule(this.skills, {
            sequential: true,
            rules: [{
                name: 'required',
                test: () => this.skillsPillList?.props.items?.length > 0,
                message: 'Gib bitte mindestens 3 Kenntnisse an.',
                sideEffects: () => {
                    this.formErrorLabels.push('no-skills');
                },
            }, {
                name: 'min',
                test: () => this.skillsPillList?.props.items?.length >= minimumSkillCount,
                message: 'Gib bitte mindestens 3 Kenntnisse an.',
                sideEffects: () => {
                    this.formErrorLabels.push('not-enough-skills');
                },
            }],
        });
    }

    validateInput(input, pillList) {
        const inputValue = input.getValue().trim();
        let valid = inputValue !== '';

        if (pillList.reduceListToArrays().find((item) => item.toLowerCase() === inputValue.toLowerCase())) {
            valid = false;
            input.setInvalid([{ noDuplicates: true }]);
            input.resetValue();
        } else {
            input.setValid();
        }

        return valid;
    }

    addItemToPillList(input, pillList, minimumRequiredItems = 1, id = null) {
        if (pillList.reduceListToArrays().length >= maximumPillListItems) {
            input.setInvalid([{ maximumPillListItems: true }]);
            return;
        }

        const inputValue = input.getValue().trim();
        if (!this.validateInput(input, pillList)) return;
        pillList.addItem(inputValue, id);
        input.resetValue();
        if (pillList.props.items.length >= minimumRequiredItems) {
            input.setValid();
        }
        // Focus after module has re-rendered
        setTimeout(() => input.setFocus(), 1);
    }

    acceptGdpr() {
        if (this.gdprModal.validate()) {
            const gdprOptionalCheckBoxSize = Object.keys(this.gdprModal.props.optionalBox.items).length;
            const gdprRequiredCheckBoxSize = Object.keys(this.gdprModal.props.requiredBox.items).length;
            const data = serializeJson(this.dom.form);

            for (let i = 0; i < gdprOptionalCheckBoxSize; i += 1) {
                // eslint-disable-next-line max-len
                data[this.gdprModal.props.optionalBox.items[i].name] = this.gdprModal.props.optionalBox.items[i].checked;
            }

            for (let i = 0; i < gdprRequiredCheckBoxSize; i += 1) {
                // eslint-disable-next-line max-len
                data[this.gdprModal.props.requiredBox.items[i].name] = this.gdprModal.props.requiredBox.items[i].checked;
            }

            this.gdprModal.closeModal();
            this.createMatching(data);
        }
    }

    closeGdprModal() {
        this.events.emit('removeModalStyle');
        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = false;
        }
    }

    showGeneralErrorBox() {
        const generalErrorBoxProps = {
            active: true,
            warning: true,
            text: 'Hm, leider scheint etwas nicht geklappt zu haben... bitte versuch es noch einmal!',
        };
        this.generalErrorBox.updateProps(generalErrorBoxProps, true);
        this.dom.generalErrorBoxContainer.scrollIntoView({ behavior: 'smooth' });
    }
}
